import { useReducer } from 'react';
import { FormField, Select, TextInput, TextArea } from 'grommet';
import { HelpWith } from '@components/basicComponents';
import { HelpWithType } from '@customTypes/enums';
import type { FormFieldsProps } from '@customTypes/types';
import type { DSSupportFormState } from '@customTypes/stateTypes';

import {
  cloudPhysicsOptions,
  helpWithOptions,
  backupAndRecoveryOptions,
  disasterRecoveryOptions,
  commonOptions,
  protectionOptions,
  severityOptions,
  timeZoneOptions,
  fileStorageOptions,
  objectServiceOptions,
  privateCloudBusinessOptions,
  storageFabricOptions,
  dataObservabilityOptions,
  primaryBlockStorageOptions,
} from './DSCCFormOptions';

export const DSCCFormFields = (props: FormFieldsProps): JSX.Element => {
  const [state, updateState] = useReducer(
    (prev: DSSupportFormState, newState: Partial<DSSupportFormState>) => ({
      ...prev,
      ...newState,
    }),
    {
      helpWith: '',
      helpWithSecondary: '',
      secondDropdownOptions: null,
      secondDropdownLabel: '',
      isLocalGatewayIDShown: false,
      isProtectionSelectShown: false,
      isTimezoneSeverityAndSubscriptionShown: false,
      severity: severityOptions.medium,
      isSerialNumberShown: false,
      isIssueTitleShown: false,
      isSubscriptionKeyShown: false,
      isTroubleshootingStepsShown: false,
    }
  );

  const onHelpWithChange = (option: string): void => {
    if (option === state.helpWith) {
      return;
    }

    clearSecondSelectState();
    updateState({
      helpWith: option,
      isSubscriptionKeyShown: false,
      isIssueTitleShown: false,
      isTroubleshootingStepsShown: false,
      secondDropdownLabel: '',
    });

    switch (option) {
      case helpWithOptions.backupAndRecovery:
        updateState({ secondDropdownOptions: backupAndRecoveryOptions });
        break;
      case helpWithOptions.blockStorage:
        updateState({
          secondDropdownOptions: primaryBlockStorageOptions,
          isSubscriptionKeyShown: true,
          isIssueTitleShown: true,
          isTroubleshootingStepsShown: true,
          secondDropdownLabel: 'Issue sub-type' });
        break;
      case helpWithOptions.objectService:
        updateState({ secondDropdownOptions: objectServiceOptions });
        break;
      case helpWithOptions.fileStorage:
        updateState({ secondDropdownOptions: fileStorageOptions });
        break;
      case helpWithOptions.privateCloudBusiness:
        updateState({ secondDropdownOptions: privateCloudBusinessOptions });
        break;
      case helpWithOptions.storageFabric:
        updateState({ secondDropdownOptions: storageFabricOptions });
        break;
      case helpWithOptions.disasterRecovery:
        updateState({ secondDropdownOptions: disasterRecoveryOptions });
        break;
      case helpWithOptions.cloudPhysics:
        updateState({ secondDropdownOptions: cloudPhysicsOptions });
        break;
      case helpWithOptions.dataObservability:
        updateState({ secondDropdownOptions: dataObservabilityOptions });
        break;
    }

    if (option === helpWithOptions.privateCloudAI) {
      updateState({ secondDropdownOptions: null });
      props.onDetailsChange({ isRequiredDetails: true, showDetails: true, numFiles: 0 });
      return;
    }

    props.onDetailsChange({ showDetails: false, isRequiredDetails: false, numFiles: 0 })
  };

  const isSerialNumberShown = (option: string, secondOption: string): boolean => {
    return [
      helpWithOptions.fileStorage,
      helpWithOptions.privateCloudBusiness,
      helpWithOptions.storageFabric,
      helpWithOptions.blockStorage
    ].includes(option) && ![
      primaryBlockStorageOptions.dashboard,
      primaryBlockStorageOptions.blockStorageAWS,
      commonOptions.other
    ].includes(secondOption);
  }

  const onHelpWithSecondaryChange = (option: string): void => {
    if (option === state.helpWithSecondary) {
      return;
    }

    clearSecondSelectState();

    updateState({
      helpWithSecondary: option,
      isLocalGatewayIDShown: option === commonOptions.localGatewayConnectivity,
      isSerialNumberShown: isSerialNumberShown(state.helpWith, option),
      isProtectionSelectShown: state.helpWith === helpWithOptions.backupAndRecovery,
      isTimezoneSeverityAndSubscriptionShown: state.helpWith === helpWithOptions.disasterRecovery,
    })

    props.onDetailsChange({ isRequiredDetails: true, showDetails: true, numFiles: 0 })
  };

  const clearState = (): void => {
    clearSecondSelectState();
    updateState({
      helpWith: '',
      secondDropdownOptions: null,
      isIssueTitleShown: false,
      isSubscriptionKeyShown: false,
      isTroubleshootingStepsShown: false,
     });

    props.onDetailsChange({ showDetails: false });
  };

  props.onReset && props.onReset(clearState);

  const clearSecondSelectState = (): void => {
    updateState({
      helpWithSecondary: '',
      isLocalGatewayIDShown: false,
      isProtectionSelectShown: false,
      isTimezoneSeverityAndSubscriptionShown: false,
      severity: severityOptions.medium,
      isSerialNumberShown: false,
    });
  }

  return (
    <>
      <HelpWith
        selectType={!props.isConsolidated ? HelpWithType.First : HelpWithType.Second}
        options={Object.values(helpWithOptions)}
        value={state.helpWith}
        onChange={onHelpWithChange}
      />

      {state.secondDropdownOptions &&
        <HelpWith
          selectType={!props.isConsolidated ? HelpWithType.Second : HelpWithType.Third}
          options={Object.values(state.secondDropdownOptions)}
          value={state.helpWithSecondary}
          label={state.secondDropdownLabel}
          onChange={onHelpWithSecondaryChange}
        />
      }

      {state.isLocalGatewayIDShown &&
        <FormField name="localGatewayID" htmlFor="local-gateway-ID" label="Local gateway ID" required>
          <TextInput id="local-gateway-ID" name="localGatewayID" placeholder="Local gateway ID"/>
        </FormField>
      }

      {state.isSerialNumberShown &&
        <FormField name="serialNumber" htmlFor="serial-number" label="Serial number" required>
          <TextInput id="serial-number" name="serialNumber" placeholder="Serial number"/>
        </FormField>
      }

      {state.isSubscriptionKeyShown &&
        <FormField name="subscriptionKey" htmlFor="subscription-key" label="Subscription key">
          <TextInput id="subscription-key" name="subscriptionKey" placeholder="Subscription key"/>
        </FormField>
      }

      {state.isIssueTitleShown &&
        <FormField name="issueTitle" htmlFor="issue-title" label="Issue title" required>
          <TextInput id="issue-title" name="issueTitle" placeholder="Issue title"/>
        </FormField>
      }

      {state.isTroubleshootingStepsShown &&
        <FormField name="troubleshootingSteps" htmlFor="troubleshooting-steps" label="Troubleshooting steps taken" required>
          <TextArea id="troubleshooting-steps" name="troubleshootingSteps" placeholder="Troubleshooting steps taken"/>
        </FormField>
      }

      {state.isProtectionSelectShown &&
        <FormField name="protectionSelect" htmlFor="protection-select" label="Is Protection Store Gateway installed?">
          <Select id="protection-select" name="protectionSelect" placeholder="Select" options={protectionOptions}/>
        </FormField>
      }

      {state.isTimezoneSeverityAndSubscriptionShown &&
        <>
          <FormField name="timeZone" htmlFor="time-zone" label="Preferred time zone" required>
            <Select id="time-zone" name="timeZone" placeholder="Select" options={timeZoneOptions}/>
          </FormField>

          <FormField name="severity" htmlFor="severity" label="Severity" required>
            <Select
              id="severity"
              name="severity"
              placeholder="Select"
              options={Object.values(severityOptions)}
              value={state.severity}
              onChange={({option}) => updateState({ severity: option })}
            />
          </FormField>

          {/* <FormField name="subscriptionKey" htmlFor="subscription-key" label="Subscription key" required>
           <TextInput id="subscription-key" name="subscriptionKey" placeholder="Subscription key"/>
          </FormField> */}
        </>
      }
    </>
  );
};
